/**
 * The line item write contract: the properties of POST /api/v5/line-items that Birdie writes, built from the field
 * types. The platform's create DTO extends it with its own properties (line-items/line-item-create.dto.ts).
 *
 * PURE.
 */
import { z } from "zod";
import {
  type ChoiceWords,
  choiceSchema,
  longTextSchema,
  moneyCentsSchema,
  quantitySchema,
  textSchema,
  toggleSchema,
  unitTypeKeySchema,
} from "./field-types";

export const LINE_ITEM_TYPES = ["PRODUCT", "SERVICE", "LABOR", "FEE"] as const;
export type LineItemType = (typeof LINE_ITEM_TYPES)[number];

export const LINE_ITEM_TYPE_META = {
  PRODUCT: { label: "Product" },
  SERVICE: { label: "Service" },
  LABOR: { label: "Labor" },
  FEE: { label: "Fee" },
} as const satisfies Record<LineItemType, ChoiceWords>;

export const lineItemTypeSchema = choiceSchema(
  LINE_ITEM_TYPES,
  LINE_ITEM_TYPE_META,
);

/** The shared properties, each exactly as the platform validates it today. */
export const lineItemWriteSchema = z.object({
  type: lineItemTypeSchema.optional(),
  name: textSchema,
  quantity: quantitySchema.optional(),
  /** The customer's price per unit, cents. */
  rate: moneyCentsSchema,
  unitTypeKey: unitTypeKeySchema,
  /** Birdwatch's cost per unit, cents. */
  cost: moneyCentsSchema.optional(),
  description: longTextSchema.optional(),
  orderScopeId: z.string().optional(),
  orderId: z.string().optional(),
  /** Unique across all line items: a repeat answers with the line already there. */
  sourceIdempotencyKey: z.string().optional(),
  catalogItemId: z.string().optional(),
  /** The customer supplies it: it keeps its rate and cost, but its price counts as 0 and its cost leaves the scope. */
  isCustomerSupplied: toggleSchema.optional(),
  /** Who does the work: an Account id (a vendor); absent for Birdwatch's own technicians. */
  vendorId: z.string().optional(),
});

export type LineItemWrite = z.input<typeof lineItemWriteSchema>;
